import { ReactElement, useEffect } from 'react'

import { useQuery } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { useSearchParams } from 'react-router-dom'

import { orgScopedApiClient } from 'api/orgScopedClient'
import { AppTypography } from 'components/AppTypography/AppTypography'
import { Flex } from 'components/core'
import { LoadingIndicator } from 'components/LoadingIndicator'
import { useCerebroApiRequest } from 'hooks'

interface ZendeskSSOTokenResponse {
    token: string
}

const getZendeskSSOToken = (): Promise<
    AxiosResponse<ZendeskSSOTokenResponse, any>
> => orgScopedApiClient.get<ZendeskSSOTokenResponse>('/api/zendesk/sso/token/')

function ZendeskSSOPage(): ReactElement {
    const [searchParams] = useSearchParams()
    const returnTo = searchParams.get('return_to')

    const makeCerebroApiRequest = useCerebroApiRequest({ throwOnError: true })

    const { data, isFetching } = useQuery({
        queryKey: ['zendeskSSOToken'],
        queryFn: () =>
            makeCerebroApiRequest({
                request: getZendeskSSOToken(),
            }),
    })

    const token = data?.data?.token

    useEffect(() => {
        if (token) {
            const queryParams = returnTo ? `?return_to=${returnTo}` : ''

            const url = `https://junglescout.zendesk.com/access/jwt${queryParams}`

            const form = document.createElement('form')
            form.method = 'POST'
            form.action = url
            form.style.display = 'none' // hide it

            const params = {
                jwt: token,
            }

            Object.entries(params).forEach(([key, value]) => {
                const input = document.createElement('input')
                input.type = 'hidden'
                input.name = key
                input.value = value
                form.appendChild(input)
            })

            document.body.appendChild(form)
            form.submit()
        }
    }, [returnTo, token])

    return (
        <Flex
            direction="column"
            justifyContent="center"
            style={{ height: '100vh' }}
        >
            {isFetching && (
                <LoadingIndicator size="medium" fullContent={false} />
            )}
            {!isFetching && !token && (
                <AppTypography>
                    Unable to retrieve SSO token. Please try again.
                </AppTypography>
            )}
            {!isFetching && token && (
                <AppTypography>Redirecting to Zendesk...</AppTypography>
            )}
        </Flex>
    )
}

export default ZendeskSSOPage
